import { type ChangeEvent, type CSSProperties } from 'react';

import type { FieldDefinition, ValidationIssue } from '@inside-us-aos/domain';

import styles from './TextInput.module.css';
import { issueMessage } from './issueMessage';

// "(XXX) XXX-XXXX" renders in 14 visible characters. Add a few ch of
// breathing room for the cursor and right padding so the closing
// digit doesn't kiss the right border. 18ch ≈ 200px at 16px/1ch on
// most fonts — comfortably wider than 14 actual chars but visibly
// narrower than the surrounding text fields.
const PHONE_INPUT_MAX_CH = 18;
const PHONE_INPUT_STYLE: CSSProperties = {
  '--field-input-max-ch': `${PHONE_INPUT_MAX_CH}ch`,
} as CSSProperties;

export interface PhoneInputProps {
  field: FieldDefinition;
  /**
   * Canonical engine value — digits only (e.g., `"4045196363"`).
   * The PDF projector writes this verbatim, so the AcroForm widget
   * stays digits-only across every consumer (I-485, I-130, I-765,
   * I-864).
   */
  value: string;
  issues: readonly ValidationIssue[];
  /** Fires with the next digits-only value. */
  onChange: (next: string) => void;
  touched?: boolean;
  onBlur?: () => void;
}

const PHONE_MAX_DIGITS = 15;

function digitsOnly(raw: string): string {
  return raw.replace(/\D/g, '').slice(0, PHONE_MAX_DIGITS);
}

function formatDisplay(digits: string): string {
  if (digits.length === 0) return '';
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  }
  if (digits.length <= 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)} ${digits.slice(10)}`;
}

export function PhoneInput({
  field,
  value,
  issues,
  onChange,
  touched = true,
  onBlur,
}: PhoneInputProps): JSX.Element {
  const inputId = `field-${field.id.replace(/\./g, '-')}`;
  const errorListId = `${inputId}-errors`;
  const showIssues = touched && issues.length > 0;
  const inputClassName = showIssues
    ? `${styles.field__input} ${styles['field__input--invalid']}`
    : styles.field__input;

  const digits = digitsOnly(value);
  const display = formatDisplay(digits);

  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    onChange(digitsOnly(event.target.value));
  };

  return (
    <div className={styles.field} data-field-id={field.id}>
      <label htmlFor={inputId} className={styles.field__label}>
        {field.label}
        {field.required === true ? (
          <span className={styles['field__required-mark']}>*</span>
        ) : null}
      </label>

      <input
        id={inputId}
        type="tel"
        inputMode="tel"
        autoComplete="tel"
        placeholder="(XXX) XXX-XXXX"
        value={display}
        onChange={handleChange}
        onBlur={onBlur}
        aria-required={field.required === true || undefined}
        aria-invalid={showIssues || undefined}
        aria-describedby={showIssues ? errorListId : undefined}
        className={inputClassName}
        style={PHONE_INPUT_STYLE}
      />

      {field.helpText !== undefined && field.helpText !== '' ? (
        <p className={styles['field__help-text']}>{field.helpText}</p>
      ) : null}

      {showIssues ? (
        <div role="alert">
          <ul id={errorListId} className={styles['field__error-list']}>
            {issues.map((issue, index) => (
              <li key={`${issue.code}-${index}`}>
                <span aria-hidden="true" className={styles['field__error-icon']}>
                  !
                </span>
                {issueMessage(issue)}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
